<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import InboxesAPI from 'dashboard/api/inboxes';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SelectInput from 'dashboard/components-next/select/Select.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const router = useRouter();
const inboxes = useMapGetter('inboxes/getInboxes');

const isLoadingPreview = ref(false);
const isSubmitting = ref(false);
const destinationId = ref('');
const preview = ref(null);
const confirmation = ref('');

// Only another Channel::Api inbox of the same account can take this one, and the store already
// holds them all.
const candidates = computed(() =>
  inboxes.value.filter(
    inbox =>
      inbox.channel_type === 'Channel::Api' && inbox.id !== props.inbox.id
  )
);

const destinationOptions = computed(() =>
  candidates.value.map(inbox => ({ value: inbox.id, label: inbox.name }))
);

const destinationName = computed(
  () =>
    candidates.value.find(inbox => inbox.id === destinationId.value)?.name || ''
);

const conflicts = computed(() => preview.value?.conflicting_source_ids ?? []);

const previewLines = computed(() => {
  if (!preview.value) return [];

  return [
    ['PREVIEW_CONVERSATIONS', preview.value.conversations],
    ['PREVIEW_MESSAGES', preview.value.messages],
    ['PREVIEW_CONTACTS', preview.value.contact_inboxes],
    ['PREVIEW_CAMPAIGNS', preview.value.campaigns],
    ['PREVIEW_MEMBERS', preview.value.inbox_members],
    ['PREVIEW_EVENTS', preview.value.reporting_events],
    ['PREVIEW_WEBHOOKS', preview.value.webhooks],
    ['PREVIEW_HOOKS', preview.value.hooks],
  ]
    .filter(([, count]) => count)
    .map(([key, count]) => t(`INBOX_MGMT.MERGE.${key}`, { count }));
});

// Typing the destination name is the confirmation: the source inbox is deleted by this.
const canSubmit = computed(
  () =>
    Boolean(destinationId.value) &&
    confirmation.value === destinationName.value &&
    conflicts.value.length === 0 &&
    !isSubmitting.value
);

const loadPreview = async () => {
  if (!destinationId.value) {
    preview.value = null;
    return;
  }

  isLoadingPreview.value = true;
  try {
    const { data } = await InboxesAPI.getMergePreview(
      props.inbox.id,
      destinationId.value
    );
    preview.value = data;
  } catch (error) {
    preview.value = null;
    useAlert(t('INBOX_MGMT.MERGE.API.ERROR_MESSAGE'));
  } finally {
    isLoadingPreview.value = false;
  }
};

watch(destinationId, () => {
  confirmation.value = '';
  loadPreview();
});

const handleMerge = async () => {
  if (!canSubmit.value) return;

  isSubmitting.value = true;
  try {
    await InboxesAPI.mergeInbox(props.inbox.id, destinationId.value);
    useAlert(t('INBOX_MGMT.MERGE.API.SUCCESS_MESSAGE'));
    router.replace({
      name: 'settings_inbox_show',
      params: { inboxId: destinationId.value },
    });
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('INBOX_MGMT.MERGE.API.ERROR_MESSAGE')
    );
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="flex flex-col gap-1">
      <h3 class="text-heading-3 text-n-slate-12">
        {{ t('INBOX_MGMT.MERGE.TITLE') }}
      </h3>
      <p class="text-body-2 text-n-slate-11">
        {{ t('INBOX_MGMT.MERGE.DESC') }}
      </p>
    </div>

    <p v-if="!candidates.length" class="text-body-2 text-n-slate-11">
      {{ t('INBOX_MGMT.MERGE.EMPTY_CANDIDATES') }}
    </p>

    <template v-else>
      <div class="flex flex-col gap-2">
        <label class="text-heading-3 text-n-slate-12">
          {{ t('INBOX_MGMT.MERGE.DEST_LABEL') }}
        </label>
        <SelectInput
          v-model="destinationId"
          :options="destinationOptions"
          :placeholder="t('INBOX_MGMT.MERGE.DEST_PLACEHOLDER')"
        />
      </div>

      <div v-if="isLoadingPreview" class="flex gap-2 items-center">
        <Spinner class="size-4 text-n-slate-11" />
        <span class="text-body-2 text-n-slate-11">
          {{ t('INBOX_MGMT.SETTINGS_POPUP.LOADING') }}
        </span>
      </div>

      <div
        v-else-if="preview"
        class="flex flex-col gap-3 p-4 rounded-lg outline outline-n-weak"
      >
        <h4 class="text-heading-4 text-n-slate-12">
          {{ t('INBOX_MGMT.MERGE.PREVIEW_TITLE') }}
        </h4>
        <ul class="pl-5 space-y-1 list-disc text-body-2 text-n-slate-11">
          <li v-for="line in previewLines" :key="line">{{ line }}</li>
          <li v-if="preview.agent_bot_inbox">
            {{ t('INBOX_MGMT.MERGE.PREVIEW_AGENT_BOT') }}
          </li>
          <li v-if="preview.inbox_assignment_policy">
            {{ t('INBOX_MGMT.MERGE.PREVIEW_ASSIGNMENT_POLICY') }}
          </li>
        </ul>

        <div
          v-if="conflicts.length"
          class="p-3 rounded-lg bg-n-amber-3 text-n-amber-12 outline outline-n-amber-6 text-body-2"
        >
          {{
            t('INBOX_MGMT.MERGE.CONFLICT_BANNER', { count: conflicts.length })
          }}
        </div>

        <div
          class="p-3 rounded-lg bg-n-ruby-3 text-n-ruby-12 outline outline-n-ruby-6 text-body-2"
        >
          {{ t('INBOX_MGMT.MERGE.WARNING_IDENTIFIER') }}
        </div>
      </div>

      <div v-if="destinationId" class="flex flex-col gap-2">
        <label class="text-heading-3 text-n-slate-12">
          {{ t('INBOX_MGMT.MERGE.CONFIRM_LABEL') }}
        </label>
        <woot-input
          v-model="confirmation"
          class="[&>input]:!mb-0"
          :placeholder="destinationName"
        />
      </div>

      <div class="flex justify-end">
        <NextButton
          :label="t('INBOX_MGMT.MERGE.SUBMIT')"
          :disabled="!canSubmit"
          :is-loading="isSubmitting"
          @click="handleMerge"
        />
      </div>
    </template>
  </div>
</template>
